import { LocalStorageKeys } from "@/enums";
import { type InternalFilters } from "@/stores/plannerStore";
import type { PlannerModuleFilterType } from "@/types";

const { PlannerFilters, Modules } = LocalStorageKeys;

const setItem = <T>(key: LocalStorageKeys, value: T) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    // oxlint-disable-next-line no-console
    console.error(
      `Failed to set item in localStorage for key "${key}":`,
      error,
    );
  }
};

const getItem = <T>(key: string): T | null => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : null;
  } catch (error) {
    // oxlint-disable-next-line no-console
    console.error(
      `Failed to get item from localStorage for key "${key}":`,
      error,
    );
    return null;
  }
};

const removeItem = (key: string) => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    // oxlint-disable-next-line no-console
    console.error(
      `Failed to remove item from localStorage for key "${key}":`,
      error,
    );
  }
};

export const filterStorageService = {
  // Filter options
  setPlannerFilters(internalFilters: any) {
    setItem(PlannerFilters, internalFilters);
  },

  removePlannerFilters() {
    removeItem(PlannerFilters);
  },

  getPlannerFilters() {
    const DEFAULT_VALUES: InternalFilters = {
      categories: [],
      departments: [],
      employees: [],
      residents: [],
      status: [],
      userTypes: [],
    };

    // Fetch stored filters or fallback to an empty object
    const fetchedFilters =
      getItem<InternalFilters>(PlannerFilters) || DEFAULT_VALUES;

    // Merge default values with fetched filters:
    // - Ensures all keys in DEFAULT_VALUES are always present.
    // - Handles cases where the cached object is outdated (e.g., missing new keys added by developers).
    // - Uses default values for any keys not found in fetchedFilters while preserving existing values.
    const mergedFilters = { ...DEFAULT_VALUES, ...fetchedFilters };

    return mergedFilters;
  },

  setModules(modules: PlannerModuleFilterType[]) {
    localStorage.setItem(Modules, JSON.stringify(modules));
  },

  removeModules() {
    removeItem(Modules);
  },

  getModules(): PlannerModuleFilterType[] {
    return getItem(Modules) || [];
  },
};
